import { type ClassValue, clsx } from "clsx";
import type { Config } from "tailwind-merge";
import { extendTailwindMerge } from "tailwind-merge";

type AdditionalClassGroupIDs =
  | "scrollbar"
  | "font-size"
  | "text-color"
  | "bg"
  | "font-family"
  | "boxShadow"
  | "size"
  | "w"
  | "h";

type AdditionalThemeGroupIDs = "colors";

const colorData = [
  "primary",
  "error",
  "accentBlue",
  "primaryMainColor",
  "secondaryMainColor",
  "thirdMainColor",
  "grayscaleWhiteColor",
  "grayscaleStrokeLightColor",
  "grayscaleStrokeDarkColor",
  "grayscaleDisabledColor",
  "grayscaleBgColor",
  "secondaryColor",
  "skeleton",
  "black",
  "darkGrey",
  "grey",
  "lightGrey",
  "lighterGrey",
  "white",
  "danger",
  {
    main: [
      {
        red: ["darker", "dark", "base", "light", "lighter"],
        warmGrey: ["base"],
      },
    ],
    secondary: [
      {
        pink: ["dark", "base", "light", "lighter"],
        green: ["dark", "base", "light", "lighter"],
        blue: ["dark", "base", "light", "lighter", "LightCyan", "lighterZero"],
        red: ["dark", "base", "light", "lighter", "LightCyan"],
        warmGrey: ["blue", "green"],
      },
    ],
    status: ["green", "orange", "red"],
    gradientColorStops: [
      {
        red: ["0", "50", "100"],
        red2: ["0", "50", "100"],
        darkRed: ["0", "50", "100"],
        blue: ["0", "50", "100"],
        green: ["0", "50", "100"],
        pink: ["0", "50", "100"],
        black: ["0", "50", "100"],
        grey: ["0", "50", "100"],
      },
    ],
    gray: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
    blue: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
    text: [
      "gotherGrayscale02",
      "gotherGrayscale03",
      "base",
      "sub",
      "placeholder",
      "disabled",
      "buttonPrimary",
      "errorMessage",
      "success",
      "productTag",
      "highlightContext",
      "highlightProduct",
      "highlightProductSecondary",
    ],
    button: [
      "primary",
      "primaryHover",
      "disabled",
      "primarySuccess",
      "textButtonPrimary",
      "textButtonSecondary",
    ],
    stroke: ["light", "dark", "active", "error"],
    background: [
      "whiteBG",
      "baigeBG",
      "lightGrayBG",
      "redSelectedBG",
      "crossSellBG",
      "darkGrayBG",
      "overlayBG",
      "searchPanelBG",
      "lightRedSelectedBG",
      "primaryRed03",
    ],
    tag: ["discountTag", "productTag", "activeTag", "conditionTag"],
    bubble: ["bubbleLight", "bubbleDark", "bubbleActive"],
  },
];
const theme: Config<AdditionalClassGroupIDs, AdditionalThemeGroupIDs>["theme"] =
  {
    colors: colorData,
  };

const classGroups: Config<
  AdditionalClassGroupIDs,
  AdditionalThemeGroupIDs
>["classGroups"] = {
  "font-size": [
    "text-xxxs",
    "text-xxs",
    "text-xs",
    "text-sm",
    "text-base",
    "text-lg",
    "text-xl",
    "text-xxl",
    "text-2xl",
    "text-3xl",
    "text-4xl",
    "text-5xl",
    "text-6xl",
  ],
  "text-color": [{ text: colorData }],
  bg: [{ bg: colorData }],
  "font-family": ["font-AdelleSansTHA", "font-ThonglorSoi4"],
  scrollbar: [{ scrollbar: ["hide", "default", "gother"] }],
  boxShadow: ["md", "lg", "shadow-main", "m", "footer", "sPlus"],
  size: Array.from({ length: 96 }).map((_res, i) => (i + 1).toString()),
  w: Array.from({ length: 96 }).map((_res, i) => (i + 1).toString()),
  h: Array.from({ length: 96 }).map((_res, i) => (i + 1).toString()),
};

const conflictingClassGroups: Config<
  AdditionalClassGroupIDs,
  AdditionalThemeGroupIDs
>["conflictingClassGroups"] = {
  size: ["w", "h"],
};
export const tailwindMergeConfig: Config<
  AdditionalClassGroupIDs,
  AdditionalThemeGroupIDs
> = {
  theme,
  classGroups,
  cacheSize: 100,
  conflictingClassGroupModifiers: {},
  conflictingClassGroups,
  separator: ":",
};
const customTwMerge = extendTailwindMerge<
  AdditionalClassGroupIDs,
  AdditionalThemeGroupIDs
>({
  cacheSize: 100,
  separator: ":",
  extend: {
    theme,
    classGroups,
    conflictingClassGroups,
  },
});

export function cn(...inputs: ClassValue[]) {
  return customTwMerge(clsx(inputs));
}
